import type { A11yTextType } from '../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type { PriceData, PriceType } from '../../../common/models/priceData/priceData';
import type { ProductPricePresentationData } from '../ProductPresentationSpecs/ProductPresentationSpecs';

import { pickProductPricePromotion } from '../../../common/components/atoms/ProductPrice/ProductPricePromotionPicker';
import { EcomProductPrice } from '../EcomProductPrice';

type ProductPrimaryPricePresentationParameters = {
    product: ProductPricePresentationData & {
        promotionPriceData?: PriceData;
        displayLowerPrice: boolean;
    };
    priceType: PriceType;
    a11yTextType: A11yTextType;
    isUserMember: boolean;
};

const getProductPrimaryPriceLabel = ({
    product,
    priceType,
    a11yTextType,
}: ProductPrimaryPricePresentationParameters) => {
    const picked = pickProductPricePromotion({
        promo: product?.promotion,
        checkUserMemberContext: false,
    });
    switch (picked.type) {
        case 'fixed':
            return {
                a11y: EcomProductPrice.getFixedPromotionTexts(picked.promotion, priceType).a11yText,
                render: () => (
                    <EcomProductPrice.FixedPromotion
                        promo={picked.promotion}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        case 'percentage':
            return {
                a11y: EcomProductPrice.getPercentagePromotionTexts({
                    isMemberPrice: picked.promotion.isMemberPrice,
                    percentage: picked.promotion.percentageDiscount,
                    newPiecePrice: product.promotionPriceData,
                    priceType,
                }).a11yText,
                render: () => (
                    <EcomProductPrice.PercentagePromotion
                        promo={picked.promotion}
                        newPiecePrice={product.promotionPriceData}
                        percentage={picked.promotion.percentageDiscount}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };

        case 'multiBuy':
            return {
                a11y: EcomProductPrice.getMultiBuyPromotionTexts(picked.promotion, priceType)
                    .a11yText,
                render: () => (
                    <EcomProductPrice.MultiBuyPromotion
                        promo={picked.promotion}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        case 'edlp': {
            return {
                a11y: EcomProductPrice.getEdlpPriceTexts(picked.promotion, priceType).a11yText,
                render: () => (
                    <EcomProductPrice.Edlp
                        promo={picked.promotion}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        }
        case 'notApplicable': {
            return {
                a11y: EcomProductPrice.getRegularPriceTexts(
                    product.priceData,
                    priceType,
                    product.displayLowerPrice,
                ).a11yText,
                render: () => (
                    <EcomProductPrice.Regular
                        displayLowerPrice={product.displayLowerPrice}
                        piecePrice={product.priceData}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        }
        default: {
            if (product.promotionPriceData) {
                return {
                    a11y: EcomProductPrice.getFlatDiscountPriceTexts(
                        product.promotionPriceData,
                        priceType,
                    ).a11yText,
                    render: () =>
                        product.promotionPriceData && (
                            <EcomProductPrice.FlatDiscount
                                newPiecePrice={product.promotionPriceData}
                                priceType={priceType}
                                a11yType={a11yTextType}
                            />
                        ),
                };
            }
            if (product.priceData) {
                return {
                    a11y: EcomProductPrice.getRegularPriceTexts(
                        product.priceData,
                        priceType,
                        product.displayLowerPrice,
                    ).a11yText,
                    render: () =>
                        product.priceData && (
                            <EcomProductPrice.Regular
                                displayLowerPrice={product.displayLowerPrice}
                                piecePrice={product.priceData}
                                priceType={priceType}
                                a11yType={a11yTextType}
                            />
                        ),
                };
            }
            return null;
        }
    }
};
export default getProductPrimaryPriceLabel;
